import React, { useEffect, useMemo, useState } from "react";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  FolderKanban,
  Layers,
  Pencil,
  Search,
} from "lucide-react";

import ContentLoader from "../../components/ContentLoader";
import { Button, Modal } from "../../components/ui";
import { dashboardToast } from "../../utils/dashboardToast";
import { getAssessmentBlueprint } from "../../services/assessmentBlueprintApi";
import type {
  AssessmentBlueprintDetail,
  AssessmentBlueprintQuestion,
} from "../../types/assessmentBlueprint";
import { normalizeAssessmentFieldsFromApi } from "../../utils/subCapabilityAssessmentConfig";
import type { BlueprintAssessmentUnit } from "../../utils/assessmentBlueprintUtils";
import { blueprintUnitName } from "../../utils/assessmentBlueprintUtils";

type Props = {
  blueprintId: number | null;
  onClose: () => void;
  onEdit: (id: number) => void;
  onDuplicate: (detail: AssessmentBlueprintDetail) => void;
};

type ViewUnit = {
  unit: BlueprintAssessmentUnit;
  name: string;
  questions: AssessmentBlueprintQuestion[];
  custom: boolean;
};

const activeQuestions = (unit: BlueprintAssessmentUnit) =>
  [...unit.currentStateQuestions, ...unit.futureStateQuestions].filter(
    (q) => q.isActive && q.questionText.trim()
  );

const isCustom = (unit: BlueprintAssessmentUnit) =>
  Boolean(
    normalizeAssessmentFieldsFromApi({ maturityOptionsJson: unit.maturityOptionsJson })
      .useCustomMaturity
  );

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

export const AssessmentBlueprintViewModal: React.FC<Props> = ({
  blueprintId,
  onClose,
  onEdit,
  onDuplicate,
}) => {
  const open = blueprintId != null;
  const [detail, setDetail] = useState<AssessmentBlueprintDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [activeCapId, setActiveCapId] = useState<number | null>(null);

  useEffect(() => {
    if (blueprintId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSearch("");
    getAssessmentBlueprint(blueprintId)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        const first = result.capabilities[0]?.id ?? null;
        setActiveCapId(first);
        setExpanded(new Set(first != null ? [first] : []));
      })
      .catch((error) => {
        if (cancelled) return;
        dashboardToast.error(error instanceof Error ? error.message : "Failed to load assessment.");
        onClose();
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprintId]);

  const capabilities = useMemo(() => {
    if (!detail) return [];
    return detail.capabilities.map((cap) => {
      const units: BlueprintAssessmentUnit[] = cap.subCapabilities.length
        ? cap.subCapabilities
        : [cap];
      const viewUnits: ViewUnit[] = units.map((unit) => ({
        unit,
        name: blueprintUnitName(unit),
        questions: activeQuestions(unit),
        custom: isCustom(unit),
      }));
      return {
        cap,
        units: viewUnits,
        hasSubs: cap.subCapabilities.length > 0,
        total: viewUnits.reduce((n, u) => n + u.questions.length, 0),
        custom: viewUnits.some((u) => u.custom),
      };
    });
  }, [detail]);

  const term = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      capabilities
        .map((c) => {
          if (!term) return c;
          const nameHit = c.cap.capabilityName.toLowerCase().includes(term);
          const units = c.units
            .map((u) => ({
              ...u,
              questions: nameHit
                ? u.questions
                : u.questions.filter((q) => q.questionText.toLowerCase().includes(term)),
            }))
            .filter((u) => u.questions.length > 0);
          return { ...c, units, total: units.reduce((n, u) => n + u.questions.length, 0) };
        })
        .filter((c) => !term || c.units.length > 0),
    [capabilities, term]
  );

  const totalQuestions = capabilities.reduce((n, c) => n + c.total, 0);
  const customCount = capabilities.filter((c) => c.custom).length;
  const defaultOptions =
    normalizeAssessmentFieldsFromApi({ maturityOptionsJson: null }).maturityOptions ?? [];

  const isOpen = (id: number) => Boolean(term) || expanded.has(id);
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allExpanded = visible.length > 0 && visible.every((c) => expanded.has(c.cap.id));

  const handleExport = () => {
    if (!detail) return;
    const rows = [["Capability", "Sub-capability", "State", "Question"]];
    for (const c of capabilities) {
      for (const u of c.units) {
        for (const q of u.questions) {
          rows.push([
            c.cap.capabilityName,
            c.hasSubs ? u.name : "",
            q.stateKind,
            q.questionText,
          ]);
        }
      }
    }
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(",")).join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${detail.title.replace(/[^\w-]+/g, "_") || "assessment"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const scrollToCap = (id: number) => {
    setActiveCapId(id);
    setExpanded((prev) => new Set(prev).add(id));
    document.getElementById(`ab-view-cap-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const statusClass =
    detail?.status === "Published"
      ? "ab-view__status ab-view__status--published"
      : "ab-view__status";

  return (
    <Modal open={open} onClose={onClose} size="4xl" className="ab-view-modal" title="Assessment">
      {loading || !detail ? (
        <ContentLoader label="Loading…" />
      ) : (
        <div className="ab-view">
          <section className="ab-view__card ab-view__hero">
            <div className="ab-view__hero-top">
              <div className="ab-view__hero-main">
                <div className="ab-view__title-row">
                  <h2 className="ab-view__title">{detail.title}</h2>
                  <span className={statusClass}>{detail.status}</span>
                </div>
                <div className="ab-view__meta">
                  <span>
                    <Layers size={14} aria-hidden /> Framework{" "}
                    <strong>{detail.sourceTraxccelFrameworkTitle || "—"}</strong>
                  </span>
                  <span>
                    <Building2 size={14} aria-hidden /> Company{" "}
                    <strong>{detail.companyName || "—"}</strong>
                  </span>
                  <span>
                    <FolderKanban size={14} aria-hidden /> Project{" "}
                    <strong>{detail.projectName || "—"}</strong>
                  </span>
                </div>
              </div>
              <div className="ab-view__actions">
                <Button variant="outline" onClick={handleExport}>
                  <Download size={16} aria-hidden />
                  Export
                </Button>
                <Button variant="outline" onClick={() => onDuplicate(detail)}>
                  <Copy size={16} aria-hidden />
                  Duplicate
                </Button>
                {detail.status === "Draft" ? (
                  <Button variant="primary" onClick={() => onEdit(detail.id)}>
                    <Pencil size={16} aria-hidden />
                    Edit
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="ab-view__stats">
              <div>
                <strong>{capabilities.length}</strong> capabilities
              </div>
              <div>
                <strong>{totalQuestions}</strong> questions
              </div>
              <div>
                <strong>{customCount}</strong>{" "}
                {customCount === 1 ? "capability" : "capabilities"} with a custom scale
              </div>
            </div>
          </section>

          <div className="ab-view__grid">
            <aside className="ab-view__card ab-view__nav">
              <div className="ab-wizard__search">
                <Search size={14} aria-hidden />
                <input
                  className="app-input"
                  type="search"
                  placeholder="Search questions…"
                  aria-label="Search questions"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <p className="ab-wizard__eyebrow">Capabilities</p>
              {visible.map((c) => (
                <button
                  key={c.cap.id}
                  type="button"
                  className={
                    activeCapId === c.cap.id
                      ? "ab-wizard__nav-item ab-wizard__nav-item--active"
                      : "ab-wizard__nav-item"
                  }
                  onClick={() => scrollToCap(c.cap.id)}
                >
                  <span>{c.cap.capabilityName}</span>
                  <span className="ab-view__nav-end">
                    {c.custom ? <span className="ab-wizard__pill ab-wizard__pill--accent">Custom</span> : null}
                    {c.total}
                  </span>
                </button>
              ))}
            </aside>

            <main className="ab-view__main">
              <div className="ab-view__main-head">
                <h3>Questions by capability</h3>
                <button
                  type="button"
                  className="ab-wizard__review-edit"
                  onClick={() =>
                    setExpanded(allExpanded ? new Set() : new Set(visible.map((c) => c.cap.id)))
                  }
                >
                  {allExpanded ? "Collapse all" : "Expand all"}
                </button>
              </div>
              {visible.length === 0 ? <p className="ab-wizard__hint">No questions match your search.</p> : null}
              {visible.map((c) => {
                const openRow = isOpen(c.cap.id);
                return (
                  <section key={c.cap.id} id={`ab-view-cap-${c.cap.id}`} className="ab-view__card ab-view__cap">
                    <button
                      type="button"
                      className="ab-view__cap-head"
                      aria-expanded={openRow}
                      onClick={() => toggle(c.cap.id)}
                    >
                      {openRow ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                      <span className="ab-view__cap-name">{c.cap.capabilityName}</span>
                      <span className={c.custom ? "ab-wizard__pill ab-wizard__pill--accent" : "ab-wizard__pill"}>
                        {c.custom ? "Custom scale" : "Default scale"}
                      </span>
                      <span className="ab-view__cap-count">
                        {c.total} {c.total === 1 ? "question" : "questions"}
                      </span>
                    </button>
                    {openRow ? (
                      <div className="ab-view__cap-body">
                        {c.units.map((u) => (
                          <div key={u.unit.id}>
                            {c.hasSubs ? <p className="ab-wizard__eyebrow ab-view__sub">{u.name}</p> : null}
                            {u.questions.length === 0 ? (
                              <p className="ab-wizard__hint">No questions.</p>
                            ) : (
                              <ol className="ab-view__questions">
                                {u.questions.map((q, i) => (
                                  <li key={q.id || i}>
                                    <span className="ab-view__qnum">{i + 1}</span>
                                    <span>{q.questionText}</span>
                                  </li>
                                ))}
                              </ol>
                            )}
                            {u.custom ? (
                              <div className="ab-view__custom-scale">
                                <p className="ab-wizard__eyebrow">Custom scale for this capability</p>
                                <div className="ab-view__custom-scale-grid">
                                  {(
                                    normalizeAssessmentFieldsFromApi({
                                      maturityOptionsJson: u.unit.maturityOptionsJson,
                                    }).maturityOptions ?? []
                                  ).map((o, i) => (
                                    <div key={`${o.optionValue}-${i}`}>
                                      <strong>
                                        {i + 1} · {o.label}
                                      </strong>
                                      <p>{o.definition}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </main>

            <aside className="ab-view__side">
              <section className="ab-view__card ab-view__panel">
                <h3>Default maturity scale</h3>
                <p className="ab-wizard__hint">
                  From {detail.sourceTraxccelFrameworkTitle || "the framework"} · used by{" "}
                  {capabilities.length - customCount}{" "}
                  {capabilities.length - customCount === 1 ? "capability" : "capabilities"}
                </p>
                <ol className="ab-view__scale">
                  {defaultOptions.map((o, i) => (
                    <li key={`${o.optionValue}-${i}`}>
                      <span className="ab-view__qnum">{i + 1}</span>
                      <span>
                        <strong>{o.label}</strong>
                        <small>{o.definition}</small>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
              <section className="ab-view__card ab-view__panel">
                <h3>About this assessment</h3>
                <p className="ab-wizard__eyebrow">Source</p>
                <p className="ab-view__about">
                  Snapshot of {detail.sourceTraxccelFrameworkTitle || "the framework"}. Later
                  framework edits don&apos;t change it.
                </p>
                <p className="ab-wizard__eyebrow">Description</p>
                <p className="ab-view__about">{detail.description?.trim() || "No description"}</p>
              </section>
            </aside>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AssessmentBlueprintViewModal;
